import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalBody,
  addToast
} from "@heroui/react";
import { Link } from 'react-router-dom';
import { Input } from 'src/shared/components/Input';
import { Button } from 'src/shared/components/Button';

import { Eye } from 'lucide-react';
import { EyeOff } from 'lucide-react';


import { setUserToken } from 'src/shared/utils/session'
import { isValidEmail } from 'src/shared/utils/validators';
import { useLazyGetCurrentUserQuery } from 'src/services/rtk_api/user/sessionApi';
import { useGetNewTokenMutation, useRegisterMutation, useConfirmUserMutation, useResendConfirmationMutation, useForgotPasswordMutation } from 'src/services/rtk_api/auth/authApi';

import LogoBlack from 'images/logo-black.svg';

import BomQuoteImage from 'images/home/bom-quote.png';
import BomQuoteIcon from 'images/home/login/Icon-Quote.svg';

const LoginModal = (props) => {
  const [mode, setMode] = useState(props.mode ? props.mode : 'LOGIN');
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    verifyCode: '',
    firstName: '',
    lastName: ''
  });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [email, setEmail] = useState(props.mode === 'VERIFY_EMAIL' ? props.credentials.username : '');
  const [password, setPassword] = useState(props.mode === 'VERIFY_EMAIL' ? props.credentials.password : '');
  const [isVisible, setIsVisible] = React.useState(false);
  const [getNewToken, {
    data: loggedUserResponse,
    isLoading: loginLoading,
    error: loginError,
    isSuccess: loginSuccess
  }] = useGetNewTokenMutation();

  const [
    registerUser,
    {
      isLoading: signupLoading,
      error: signupError,
      isSuccess: signupSuccess,
    }
  ] = useRegisterMutation();

  const [
    getCurrentUser,
    {
      data: currentUser,
      isLoading: currentUserLoading,
      error: currentUserError,
      isSuccess: currentUserSuccess
    }
  ] = useLazyGetCurrentUserQuery();

  const [confirmUser, { isLoading: confirming, error: confirmError }] = useConfirmUserMutation();
  const [resendConfirmation] = useResendConfirmationMutation();

  const {
    // show,
    isOpen,
    onOpenChange,
    onOk
  } = props;

  const toggleVisibility = () => setIsVisible(!isVisible);

  const changeMode = (newMode) => {
    if(newMode !== 'VERIFY_EMAIL') {
      setEmail('');
      setPassword('');
    }
    setErrors({
      email: '',
      password: '',
      verifyCode: '',
      firstName: '',
      lastName: ''
    });
    setMode(newMode);
  }

  const hasErrors = () => {    
    if (mode === 'VERIFY_EMAIL') {
      if (!verifyCode.trim()) {
        setErrors((prev) => ({ ...prev, verifyCode: 'Verification code is required' }));
        return true;
      }
      return false;
    } else {
      let hasError = false;
      let newErrors = { email: '', password: '' };

      if (mode === 'SIGNUP') {
        newErrors = { email: '', password: '', firstName: '', lastName: '' };

        if (!firstName.trim()) {
          newErrors.firstName = 'First name is required';
          hasError = true;
        }

        if (!lastName.trim()) {
          newErrors.lastName = 'Last name is required';
          hasError = true;
        }
      }

      if (!email.trim()) {
        newErrors.email = 'Email is required';
        hasError = true;
      } else if (!isValidEmail(email)) {
        newErrors.email = 'Enter a valid email address';
        hasError = true;
      }

      if (!password.trim()) {
        newErrors.password = 'Password is required';
        hasError = true;
      }

      setErrors(newErrors);
      return hasError;
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if(hasErrors()) return;
    switch (mode) {
      case 'LOGIN':
        try {
          await getNewToken({ grant_type: 'password', username: email, password: password }).unwrap();
        } catch (err) {
          if (err?.data?.error === 'email_unconfirmed') {
            changeMode('VERIFY_EMAIL');
          } else {
            setErrors({
              email: 'Login failed. Please verify your username and password',
              password: 'Login failed. Please verify your username and password'
            });
          }
          
        }
        break;
      case 'SIGNUP':
        try {
          await registerUser({
            email,
            password,
            first_name: firstName,
            last_name: lastName
          }).unwrap();
          changeMode('VERIFY_EMAIL');
        } catch (err) {
          setErrors({
            email: err?.data?.errors?.[0] || 'Sign up failed, contact support'
          });
        }
        break;
      case 'VERIFY_EMAIL':
        try {
          await confirmUser({ email, code: verifyCode }).unwrap();
          await getNewToken({ grant_type: 'password', username: email, password }).unwrap();
        } catch (err) {
          setErrors((prev) => ({ ...prev, verifyCode: err?.data?.error || 'Verification failed' }));
        }
        break;
      default:
        console.error('Unknown mode:', mode);
    }
  }

  useEffect(() => {
    if (loginError) {
      addToast({
        title: loginError.data.message,
        severity: "danger",
        color: "danger"
      });
    }
    if (loginSuccess && loggedUserResponse?.access_token) {
      const { access_token, refresh_token, expires_in } = loggedUserResponse;
      setUserToken(access_token, refresh_token, expires_in);
      getCurrentUser();
      onOk();
      onOpenChange(false);
    }
  }, [loginSuccess, loginError]);

  useEffect(() => {
    if (!isOpen) {
      changeMode('LOGIN');
    }
  }, [isOpen]);

  const FormInputs = (direction = 'flex-col') => {
    return (
      <div className={`flex ${direction} gap-4`}>
        <Input
          type="email"
          label="Email"
          labelPlacement="outside"
          placeholder=" "
          value={email}
          name="email"
          onValueChange={(val) => {
            setEmail(val);
            setErrors((prev) => ({ ...prev, email: '', password: prev.email === 'Login failed. Please verify your username and password' ? '' : prev.password }));
          }}
          variant="bordered" 
          isInvalid={!!errors.email}
          errorMessage={errors.email !== 'Login failed. Please verify your username and password' ? errors.email : ''}/>

        <Input
          label="Password"
          name="password"
          labelPlacement="outside"
          placeholder=" "
          value={password}
          onValueChange={(val) => {
            setPassword(val);
            setErrors((prev) => ({ ...prev, password: '', email: prev.password === 'Login failed. Please verify your username and password' ? '' : prev.email }));
          }}
          endContent={
            <button className="focus:outline-none" type="button" onClick={toggleVisibility}
                    aria-label="toggle password visibility">
              {isVisible ? (
                <Eye size={16} className="text-lg text-sky-700 pointer-events-none" />
              ) : (
                <EyeOff size={16} className="text-lg text-sky-700 pointer-events-none" />
              )}
            </button>
          }
          type={isVisible ? "text" : "password"}
          variant="bordered"
          isInvalid={!!errors.password}
          errorMessage={errors.password !== 'Login failed. Please verify your username and password' ? errors.password : '' }
        />
      </div>)
  }

  const BomQuoteAd = () => {
    return (<div className="quote-ad-container">
            <div className="image-container">
              <img src={BomQuoteImage} alt="Bom Quote" className="bom-quote-image" />
              <div className="quote-icon-container">
                <img src={BomQuoteIcon} alt="Bom Quote Icon" className="bom-quote-icon" />
                <p className="text-[#283546] text-[18px] font-bold leading-[22px]">BOM Quote</p>
              </div>
            </div>
            <p className="text-[#007CA1] text-[28px] text-center font-extrabold leading-[18px]">Save time and money!</p>
            <div className='px-10 mt-auto mb-4'>
              <p className="text-[#818F99] text-center text-[14px] font-normal tracking-[-0.28px]">By using our application, you agree to our <a href='/privacy-policy' target='_blank' className='text-[#007CA1]'>Privacy Policy</a> and <a href='/terms-and-conditions' target='_blank' className='text-[#007CA1]'>Terms of Service</a></p>
            </div>
          </div>)
  }

  const LoginModalContent = () => {
    return (<>
      <ModalBody style={{ padding: 'unset' }}>
        <div className="form-container" id="log-in-form-content">
          <form className="form-signin">
            <div className="form-header !mb-[24px]">
              <h2 className="!font-[Manrope] font-extrabold text-[30px] leading-[42px] tracking-[-0.02em] text-[#283546]">Sign in</h2>
              { process.env.REACT_APP_SIGNUP_ENABLED === 'true' && 
                <div className='text-[#293538] text-[16px] font-normal mt-2 flex flex-row gap-4'>New user? <div className="text-[#007CA1] text-[16px] underline cursor-pointer" onClick={() => changeMode("SIGNUP")}>Create a New Account</div></div>
              }
            </div>
            { FormInputs() }
            { errors.email === 'Login failed. Please verify your username and password' &&
              <p className="text-center text-[#f31260] text-sm mt-2">{errors.email}</p>
            }
            <div className="mt-8 mb-5">
              <Button type="submit" className="w-full text-center bg-[#007CA1] text-white hover:bg-[#006885]" onClick={onSubmit}>
                {loginLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>
            <div className="flex flex-col gap-2 mt-3">
              <Link
                className="forget-password mb-1 !text-[#293538]"
                to="/reset-password"
                onClick={() => onOpenChange(false)}
              >
                Forgot Password?
              </Link>
            </div>
          </form>
        </div>
      </ModalBody>
    </>)
  }

  const SignupModalContent = () => {
    return (<>
      <ModalBody style={{ padding: 'unset' }}>
        <div className="form-container" id="sign-up-form-content">
          <form className="form-signup">
            <div className="form-header mt-2 mb-6 flex flex-col gap-3">
              <h2 className="text-[#283546] text-[30px] not-italic font-extrabold leading-[42px] tracking-[-0.6px]">Create an Account</h2>
              <p className="text-[#FF6229] text-[20px] font-extrabold">50 Free Credit to Sign Up!</p>
            </div>
            <div className={`flex flex-row gap-4 mb-6`}>
              <Input
                type="text"
                label="First Name"
                labelPlacement="outside"
                placeholder=" "
                value={firstName}
                name="first_name"
                onValueChange={setFirstName}
                variant="bordered"
                isInvalid={!!errors.firstName}
                errorMessage={errors.firstName} />

              <Input
                label="Last Name"
                name="last_name"
                labelPlacement="outside"
                placeholder=" "
                value={lastName}
                onValueChange={setLastName}
                type="text"
                variant="bordered"
                isInvalid={!!errors.lastName}
                errorMessage={errors.lastName}/>
            </div>
            { FormInputs('flex-row') }
            <div className="actions-container flex md:flex-row flex-col mt-10 gap-2 md:gap-6">
              <Button type="submit" className="w-[300px] text-center bg-[#007CA1] text-white hover:bg-[#006885]" onClick={onSubmit}>
                {signupLoading ? 'Signing up...' : 'Sign up'}
              </Button>
              <div className='text-[#293538] text-[14px] font-normal mt-2 flex flex-row gap-4'>Already have an account? <div className="text-[#007CA1] text-[14px] underline cursor-pointer" onClick={() => changeMode("LOGIN")}>Sign in</div></div>
            </div>
          </form>
          { BomQuoteAd() }
        </div>
      </ModalBody>
    </>)
  }

  const VerifyModalContent = () => {
    return (<>
      <ModalBody style={{ padding: 'unset' }}>
        <div className="form-container" id="sign-up-form-content">
          <form className="form-signup">
            <div className="form-header mt-2 mb-6 flex flex-col gap-6">
              <h2 className="text-[#283546] text-[30px] not-italic font-extrabold leading-[42px] tracking-[-0.6px]">Verify With Your Email</h2>
              <p className="text-[#5A6677] text-[16px] w-full md:w-[588px]">You will receive a link email shortly. Please be sure to check all folders including spam for the email.</p>
            </div>
            <div className={`flex flex-row gap-4 mb-6 items-baseline`}>
              <Input
                type="tel"
                label="Enter Code"
                labelPlacement="outside"
                placeholder=" "
                value={verifyCode}
                name="code"
                inputMode="numeric"
                maxLength={8}
                onValueChange={setVerifyCode}
                variant="bordered"
                className='w-1/2'
                isInvalid={!!errors.verifyCode}
                errorMessage={errors.verifyCode} />

              <Button type="submit" className="w-1/2 text-center bg-[#007CA1] text-white hover:bg-[#006885]" onClick={onSubmit}>
                {loginLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </form>
          { BomQuoteAd() }
        </div>
      </ModalBody>
    </>)
  }

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} className={mode === "LOGIN" ? "login-modal" : "signup-modal"} isKeyboardDismissDisabled={true}>
      <ModalContent>
        {mode === "LOGIN" && LoginModalContent()}
        {mode === "SIGNUP" && SignupModalContent()}
        {mode === "VERIFY_EMAIL" && VerifyModalContent()}
      </ModalContent>
    </Modal>
  )
}

export default LoginModal;